import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Full gallery — every image browsable; active slide drives variant sync on the right.
 */
export default function ProductGalleryPremium({
  images = [],
  focusIndex = 0,
  syncToken = 0,
  activeVariantLabel = '',
  onActiveImageChange,
  className = '',
}) {
  const validImages = (images || []).filter(Boolean);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);
  const thumbStripRef = useRef(null);
  const skipNotifyRef = useRef(false);
  const didInitialNotify = useRef(false);
  const count = validImages.length;

  const notifyActiveImage = useCallback(
    (index) => {
      if (skipNotifyRef.current || !onActiveImageChange) return;
      const safe = Math.max(0, Math.min(index, count - 1));
      onActiveImageChange(safe, validImages[safe]);
    },
    [count, onActiveImageChange, validImages]
  );

  const goTo = useCallback(
    (index, { smooth = true, notify = false } = {}) => {
      const next = Math.max(0, Math.min(index, count - 1));
      setActiveIndex(next);
      const el = scrollRef.current;
      if (el) {
        el.scrollTo({
          left: next * el.offsetWidth,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
      const thumb = thumbStripRef.current?.children[next];
      thumb?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'nearest',
        inline: 'center',
      });
      if (notify) {
        notifyActiveImage(next);
      }
    },
    [count, notifyActiveImage]
  );

  const imagesKey = validImages.join('|');

  useEffect(() => {
    setActiveIndex(0);
    scrollRef.current?.scrollTo({ left: 0, behavior: 'auto' });
    didInitialNotify.current = false;
  }, [imagesKey]);

  useEffect(() => {
    if (syncToken === 0 || count === 0) return;
    skipNotifyRef.current = true;
    const target = Math.max(0, Math.min(focusIndex, count - 1));
    goTo(target, { smooth: syncToken > 1, notify: false });
    const timer = window.setTimeout(() => {
      skipNotifyRef.current = false;
    }, 450);
    return () => window.clearTimeout(timer);
  }, [syncToken, focusIndex, count, goTo]);

  useEffect(() => {
    if (count > 0 && onActiveImageChange && !didInitialNotify.current) {
      didInitialNotify.current = true;
      notifyActiveImage(activeIndex);
    }
  }, [count, onActiveImageChange, activeIndex, notifyActiveImage]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || count <= 1) return;
    const index = Math.round(el.scrollLeft / el.offsetWidth);
    if (index !== activeIndex && index >= 0 && index < count) {
      setActiveIndex(index);
      const thumb = thumbStripRef.current?.children[index];
      thumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      notifyActiveImage(index);
    }
  }, [activeIndex, count, notifyActiveImage]);

  if (count === 0) {
    return (
      <div
        className={`w-full aspect-[4/5] max-h-[min(70vw,420px)] rounded-lg bg-slate-100 flex items-center justify-center ${className}`}
      >
        <span className="text-xs text-slate-400">No image</span>
      </div>
    );
  }

  const showControls = count > 1;

  return (
    <div className={`w-full min-w-0 flex flex-col gap-2 ${className}`}>
      <div className="relative group w-full overflow-hidden rounded-lg">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className={`flex w-full overflow-x-auto snap-x snap-mandatory scrollbar-hide bg-slate-50 border border-slate-100 ${
            showControls ? 'scroll-smooth' : ''
          }`}
          style={{ touchAction: 'pan-x' }}
        >
          {validImages.map((image, index) => (
            <div
              key={`slide-${image}-${index}`}
              className="w-full shrink-0 snap-center snap-always aspect-[4/5] sm:aspect-[5/6] max-h-[min(72vw,360px)] sm:max-h-[min(60vw,400px)] md:max-h-[min(48vh,420px)]"
            >
              <img
                src={image}
                alt={`Product view ${index + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>
          ))}
        </div>

        {showControls && (
          <>
            <span className="absolute top-2 right-2 z-10 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white tabular-nums pointer-events-none">
              {activeIndex + 1} / {count}
            </span>

            <button
              type="button"
              onClick={() => goTo(activeIndex - 1, { smooth: true, notify: true })}
              disabled={activeIndex === 0}
              className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 shadow-md border border-slate-200 flex items-center justify-center disabled:opacity-25"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => goTo(activeIndex + 1, { smooth: true, notify: true })}
              disabled={activeIndex === count - 1}
              className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/95 shadow-md border border-slate-200 flex items-center justify-center disabled:opacity-25"
              aria-label="Next image"
            >
              <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {activeVariantLabel && (
        <p className="text-[10px] sm:text-[11px] text-slate-500 px-0.5 truncate">
          Viewing:{' '}
          <span className="font-semibold text-brand-700">{activeVariantLabel}</span>
        </p>
      )}

      {showControls && (
        <div
          ref={thumbStripRef}
          className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-0.5 scrollbar-hide snap-x snap-mandatory scroll-smooth"
          role="tablist"
          aria-label="All product images"
        >
          {validImages.map((image, index) => {
            const isActive = activeIndex === index;
            return (
              <button
                key={`thumb-${image}-${index}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Image ${index + 1} of ${count}`}
                onClick={() => goTo(index, { smooth: true, notify: true })}
                className={`snap-start shrink-0 rounded-md overflow-hidden transition-all duration-300 ease-out
                  w-12 h-12 sm:w-14 sm:h-14 md:w-[4.5rem] md:h-[4.5rem]
                  ${
                    isActive
                      ? 'border-2 border-brand-600 ring-2 ring-brand-500/40 opacity-100 scale-[1.04] shadow-sm'
                      : 'border-2 border-slate-200 opacity-80 hover:opacity-100 hover:border-brand-400'
                  }`}
              >
                <img src={image} alt="" className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}

      {showControls && (
        <p className="text-[9px] text-slate-400 text-center">
          Swipe or tap any image — variant updates automatically
        </p>
      )}
    </div>
  );
}
